import { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface ConfirmDialogProps {
  title: string;
  message: string;
  /** Optional detail lines shown in a warning box below the message */
  details?: string[];
  confirmLabel?: string;
  cancelLabel?: string;
  /** "danger" shows red confirm button, "warning" shows amber */
  variant?: 'danger' | 'warning';
  onConfirm: () => Promise<void> | void;
  onCancel: () => void;
}

export function ConfirmDialog({
  title,
  message,
  details,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  variant = 'danger',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const [loading, setLoading] = useState(false);

  async function handleConfirm() {
    setLoading(true);
    try {
      await onConfirm();
    } finally {
      setLoading(false);
    }
  }

  const isDanger = variant === 'danger';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header with gradient band */}
        <div className={`px-6 py-4 flex items-center justify-between ${isDanger ? 'bg-red-500' : 'bg-amber-500'}`}>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-white" />
            <h3 className="text-sm font-bold text-white uppercase tracking-wide">
              {title}
            </h3>
          </div>
          <button onClick={onCancel} className="text-white/60 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 max-h-[calc(100vh-8rem)] overflow-y-auto">
          <p className="text-sm text-slate-600 leading-relaxed">{message}</p>

          {details && details.length > 0 && (
            <div className={`rounded-lg p-3 text-sm ${isDanger ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'}`}>
              <ul className="space-y-1">
                {details.map((d, i) => (
                  <li key={i} className={`flex items-start gap-2 ${isDanger ? 'text-red-700' : 'text-amber-700'}`}>
                    <span className="mt-0.5 shrink-0">•</span>
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
              {cancelLabel}
            </Button>
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={loading}
              className={isDanger
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-amber-500 text-white hover:bg-amber-600'
              }
            >
              {loading ? 'Please wait...' : confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
